import { LoadingSpinner } from "@/components/common/LoadingSpinner/LoadingSpinner"
import { ModalBase } from "@/components/common/ModalBase/ModalBase"
import { toast } from "@/components/common/Toast"
import { Typography } from "@/components/common/Typography/Typography"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import {
  knowledgebaseApiCalls,
  useGetKnowledgebaseFile,
} from "@/services/api/knowledgebaseService"
import { Citation } from "@/types/questionnaire/models"
import { downloadFile } from "@/utils/downloadUtils"
import { IconExternalLink, IconX } from "@tabler/icons-react"
import { useCallback, useEffect, useState } from "react"
import { useNavigate } from "react-router"
import { PdfHighlightViewer } from "./PdfHighlightViewer"

interface FilePreviewModalWithPdfViewerProps {
  urlQueryId?: string
  citation?: Citation
  fullScreen?: boolean
  setIsPreviewModalOpen: (isOpen: boolean) => void
}

export const FilePreviewModalWithPdfViewer: React.FC<
  FilePreviewModalWithPdfViewerProps
> = ({ urlQueryId, citation, setIsPreviewModalOpen, fullScreen = false }) => {
  const { translate } = useTranslation()
  const navigate = useNavigate()

  const [previewUrl, setPreviewUrl] = useState<string>()
  const [isLoadingContent, setIsLoadingContent] = useState(false)

  const {
    isPending: isFetchFilePending,
    isError: isFetchFileError,
    data: fileData,
  } = useGetKnowledgebaseFile(urlQueryId)

  const closePreviewModal = useCallback(() => {
    const searchParams = new URLSearchParams(window.location.search)
    searchParams.delete("file")
    navigate(`${window.location.pathname}?${searchParams.toString()}`, {
      replace: true,
    })
    setIsPreviewModalOpen(false)
  }, [navigate, setIsPreviewModalOpen])

  useEffect(() => {
    if (isFetchFileError) {
      toast.error(translate("KNOWLEDGEBASE/PREVIEW_FILE_ERROR"))
      closePreviewModal()
      return
    }

    if (
      fileData &&
      !isFetchFilePending &&
      urlQueryId &&
      !previewUrl &&
      !isLoadingContent
    ) {
      if (fileData?.mimetype !== "application/pdf") {
        if (fileData?.url) {
          downloadFile(fileData.url, fileData.fileName)
        }
        closePreviewModal()
        toast.success(translate("KNOWLEDGEBASE/DOWNLOAD_FILE_SUCCESS"))
      } else {
        setIsLoadingContent(true)
        knowledgebaseApiCalls
          .getFileContent(urlQueryId)
          .then((arrayBuffer) => {
            const blob = new Blob([arrayBuffer], { type: "application/pdf" })
            const blobUrl = URL.createObjectURL(blob)
            setPreviewUrl(blobUrl)
          })
          .catch(() => {
            toast.error(translate("KNOWLEDGEBASE/PREVIEW_FILE_ERROR"))
            closePreviewModal()
          })
          .finally(() => {
            setIsLoadingContent(false)
          })
      }
    }
  }, [
    fileData,
    isFetchFileError,
    isFetchFilePending,
    urlQueryId,
    previewUrl,
    isLoadingContent,
    translate,
    closePreviewModal,
  ])

  useEffect(() => {
    return () => {
      if (previewUrl && previewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(previewUrl)
      }
    }
  }, [previewUrl])

  return (
    <ModalBase showSidebarLeft fullScreen={fullScreen} overshadowContentRight>
      <div className="flex h-full flex-col">
        <div className="border-gray-200 bg-gray-50 flex items-center justify-between border-b px-2 py-1">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            {citation ? (
              <>
                <Typography
                  variant="textXS"
                  className="shrink-0 text-grey5"
                >
                  {translate("QUESTIONNAIRE_ANSWERS/CITATION_TITLE")}:
                </Typography>
                <Typography variant="textXS" className="truncate text-grey5">
                  {translate("QUESTIONNAIRE_ANSWERS/CITATION_PAGE_LABEL", {
                    fileName: citation.sourceFileName,
                    page: citation.sourceFilePage,
                  })}
                  {citation.sourceText && (
                    <span className="text-gray-400 ml-2">
                      {translate(
                        "QUESTIONNAIRE_ANSWERS/CITATION_SEARCHING_LABEL",
                        { text: citation.sourceText },
                      )}
                    </span>
                  )}
                </Typography>
              </>
            ) : null}
          </div>
          <div className="ml-4 flex gap-1">
            {previewUrl && (
              <button
                onClick={() =>
                  window.open(
                    citation?.sourceFilePage
                      ? `${previewUrl}#page=${citation.sourceFilePage}`
                      : previewUrl,
                    "_blank",
                  )
                }
                className="hover:bg-gray-200 flex items-center gap-1 rounded p-1.5 text-xs"
                title={translate("OPEN_IN_NEW_TAB")}
              >
                <IconExternalLink size={16} />
              </button>
            )}
            <button
              onClick={closePreviewModal}
              className="hover:bg-gray-200 flex items-center gap-1 rounded p-1.5 text-xs"
              title={translate("CLOSE")}
            >
              <IconX size={16} />
            </button>
          </div>
        </div>

        <div className="min-h-0 flex-1">
          {!isFetchFilePending && !isLoadingContent && previewUrl ? (
            <PdfHighlightViewer
              fileUrl={previewUrl}
              initialPage={citation?.sourceFilePage}
              searchText={citation?.sourceText}
              boundingRegions={citation?.boundingRegions}
            />
          ) : (
            <div className="flex h-full items-center justify-center">
              <LoadingSpinner />
            </div>
          )}
        </div>
      </div>
    </ModalBase>
  )
}
